import React, { useState } from 'react';
import { Select, MenuItem, Chip, CircularProgress, Box, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

/**
 * Inline "who owns this task" control for the Tasks table.
 *
 * Assignment is suggested by the extraction pipeline and can be corrected
 * here. Three states worth distinguishing:
 *   • auto-assigned  — a team, with a sparkle marking it as the AI's guess
 *   • manually set   — a team, no sparkle; reprocessing will not touch it
 *   • unassigned     — dashed outline, matching the Due Date cell's existing
 *                      "needs attention" affordance
 *
 * Choosing "Unassigned" is a deliberate act and is remembered: the pipeline
 * won't re-assign it on the next reprocess. "Reset to automatic" forgets the
 * manual choice so the next reprocess can suggest again.
 */
const UNASSIGNED = '__unassigned__';
const RESET = '__reset__';

const TaskTeamPicker = ({ contractId, task, teams, onChanged }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const teamId = task.team_id || null;
  const isAuto = Boolean(teamId) && !task.team_is_manual;

  const save = async (body) => {
    setSaving(true);
    setError('');
    try {
      const url = `${API_BASE_URL}/api/contracts/${contractId}/tasks/${encodeURIComponent(task.id)}/team`;
      const r = await axios.patch(url, body);
      onChanged?.(task.id, r.data);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    if (v === RESET) return save({ reset: true });
    return save({ team_id: v === UNASSIGNED ? null : v });
  };

  if (saving) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: 32, pl: 1 }}>
        <CircularProgress size={14} />
      </Box>
    );
  }

  return (
    <Tooltip title={error || (isAuto ? 'Suggested during document processing' : '')}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {isAuto && (
          <AutoAwesomeIcon sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0 }} />
        )}
        <Select
          value={teamId || UNASSIGNED}
          onChange={handleChange}
          size="small"
          displayEmpty
          renderValue={(v) => {
            if (v === UNASSIGNED) {
              return <Box component="span" sx={{ color: 'text.disabled' }}>Unassigned</Box>;
            }
            // Falls back to the name the server sent, so a team that was
            // deleted or isn't in the list still shows something meaningful.
            return teams.find((t) => t.id === v)?.name || task.team_name || 'Unknown team';
          }}
          sx={{
            width: 150,
            height: 32,
            fontSize: '0.78rem',
            ...(teamId ? {} : {
              '& fieldset': { borderColor: 'rgba(230,126,34,0.4)', borderStyle: 'dashed' },
              '&:hover fieldset': { borderColor: '#e67e22' },
            }),
            ...(error ? { '& fieldset': { borderColor: 'error.main' } } : {}),
          }}
        >
          <MenuItem value={UNASSIGNED} sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
            Unassigned
          </MenuItem>
          {teams.map((t) => (
            <MenuItem key={t.id} value={t.id} sx={{ fontSize: '0.78rem' }}>
              {t.name}
            </MenuItem>
          ))}
          {task.team_is_manual && (
            <MenuItem value={RESET} sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              <Chip
                icon={<AutoAwesomeIcon sx={{ fontSize: 11 }} />}
                label="Reset to automatic"
                size="small"
                sx={{ height: 20, fontSize: '0.68rem' }}
              />
            </MenuItem>
          )}
        </Select>
      </Box>
    </Tooltip>
  );
};

export default TaskTeamPicker;
